"use client";

import React from "react";
import { LocaleLink } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { ArrowRight } from "lucide-react";

/**
 * Sticky bottom CTA (mobile only). The page reserves pb-20 on mobile.
 * Market-test: part of the compact-checkout funnel — click carries
 * `wf_compact_checkout_v1` (market-test-plan.md §3/§6). Price hook is
 * data-driven from the real catalog floor (no hardcoded number).
 */
const STICKY_EXPERIMENT_ID = "wf_compact_checkout_v1";

export default function StickyCTA({ fromPrice }: { fromPrice?: string }) {
  const { t } = useTranslations();

  const onClick = () => {
    try {
      track({
        event_name: "sticky_cta_clicked",
        event_id: `sticky_cta_clicked:${STICKY_EXPERIMENT_ID}:${Date.now()}`,
        experiment_id: STICKY_EXPERIMENT_ID,
        variant_id: "mobile_sticky",
      });
    } catch {
      /* analytics is best-effort */
    }
  };

  const fromLabel = fromPrice
    ? t("cobra.sticky.from", "From {price} · instant eSIM").replace("{price}", fromPrice)
    : t("cobra.sticky.fromGeneric", "Instant eSIM · 180+ countries");

  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t-2 border-foreground bg-card px-4 py-3 flex items-center justify-between gap-3">
      <div className="leading-tight">
        <div className="text-[11px] font-bold uppercase tracking-tight text-foreground/70">
          {fromLabel}
        </div>
        <div className="display-xl text-lg">{t("cobra.sticky.title", "Travel connected")}</div>
      </div>
      <LocaleLink
        href="/destinations/"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 border-2 border-foreground bg-primary text-primary-foreground font-bold uppercase tracking-tight px-4 h-11 shadow-[3px_3px_0_0_hsl(var(--foreground))] shrink-0"
      >
        {t("cobra.sticky.cta", "Get started")} <ArrowRight className="w-4 h-4" />
      </LocaleLink>
    </div>
  );
}
